import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { HelpArticle } from '../types/help';

interface HelpContextType {
  isHelpPanelOpen: boolean;
  isCommandPaletteOpen: boolean;
  selectedArticle: HelpArticle | null;
  openHelpPanel: (searchQuery?: string) => void;
  closeHelpPanel: () => void;
  openCommandPalette: () => void;
  closeCommandPalette: () => void;
  selectArticle: (article: HelpArticle) => void;
  toggleHelp: () => void;
  navigateAndClose: (path: string) => void;
}

const HelpContext = createContext<HelpContextType | undefined>(undefined);

export const HelpProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const [isHelpPanelOpen, setIsHelpPanelOpen] = useState(false);
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState<HelpArticle | null>(null);

  const openHelpPanel = useCallback((searchQuery?: string) => {
    setIsHelpPanelOpen(true);
    setIsCommandPaletteOpen(false);
  }, []);

  const closeHelpPanel = useCallback(() => {
    setIsHelpPanelOpen(false);
    setSelectedArticle(null);
  }, []);

  const openCommandPalette = useCallback(() => {
    setIsCommandPaletteOpen(true);
    setIsHelpPanelOpen(false);
  }, []);

  const closeCommandPalette = useCallback(() => {
    setIsCommandPaletteOpen(false);
  }, []);

  const selectArticle = useCallback((article: HelpArticle) => {
    setSelectedArticle(article);
    setIsHelpPanelOpen(true);
    setIsCommandPaletteOpen(false);
  }, []);

  const toggleHelp = useCallback(() => {
    if (isHelpPanelOpen) {
      closeHelpPanel();
    } else {
      openHelpPanel();
    }
  }, [isHelpPanelOpen, openHelpPanel, closeHelpPanel]);

  const navigateAndClose = useCallback((path: string) => {
    closeHelpPanel();
    closeCommandPalette();
    navigate(path);
    // Reopen help panel after navigation
    setTimeout(() => {
      setIsHelpPanelOpen(true);
    }, 300);
  }, [navigate, closeHelpPanel, closeCommandPalette]);

  // Cmd+K / Ctrl+K to open command palette
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        openCommandPalette();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [openCommandPalette]);

  return (
    <HelpContext.Provider
      value={{
        isHelpPanelOpen,
        isCommandPaletteOpen,
        selectedArticle,
        openHelpPanel,
        closeHelpPanel,
        openCommandPalette,
        closeCommandPalette,
        selectArticle,
        toggleHelp,
        navigateAndClose,
      }}
    >
      {children}
    </HelpContext.Provider>
  );
};

export const useHelp = (): HelpContextType => {
  const context = useContext(HelpContext);
  if (context === undefined) {
    throw new Error('useHelp must be used within a HelpProvider');
  }
  return context;
};
